/**
 * Output figures derived from the resolved scene.
 *
 * - Floor area: room minus walls (counters do NOT reduce it).
 * - Perimeter: sum of all boundary segment lengths (outer + wall holes).
 * - Wall area: Σ segment length × wall height (the ceiling), internal wall
 *   elements included.
 * - Area by type + unfloored area come from the painter resolution; built-ins
 *   on the floor and against the walls come out of what they cover.
 * - Trim: doors and windows are cased on this room's side, windows have sills.
 */

import { builtInWallCoverage } from './builtInWallCoverage';
import { multiPolygonArea } from './geometry';
import { lineHosts } from './refs';
import { Segment } from './resolve';
import { resolveScene } from './scene';
import { FloorPlanEdgeSegmentSimple, FloorPlanRoom, isKnownEdgeSegmentType, roomCeilingHeight, roomFreeElements, segmentOpeningBase } from './schemas';

export interface AreaByType {
  type: string;
  area: number;
}

export interface MeasurementResult {
  totalFloorArea: number; // ft²; room minus walls
  perimeter: number; // ft; wall run at the floor, less doors and floor-level openings
  wallArea: number; // ft²; less openings and the faces of built-ins against the wall
  areaByType: AreaByType[];
  unflooredArea: number; // ft²; footprint of counters and every other built-in on the floor
  baseboardLength: number; // ft; the perimeter, less built-ins standing against the wall
  crownLength: number; // ft; wall run at the ceiling, less openings that reach it
  doorTrimLength: number; // ft; casing on this room's side of each door: both sides and the head
  windowTrimLength: number; // ft; casing around each window
  windowSillLength: number; // ft
  countertopArea: number; // ft²
}

const segmentLength = (s: Segment): number => {
  return Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
};

export const perimeterLength = (segments: readonly Segment[]): number => {
  return segments.reduce((sum, s) => sum + segmentLength(s), 0);
};

export const wallAreaOf = (segments: readonly Segment[]): number => {
  return segments.reduce((sum, s) => sum + segmentLength(s) * s.height, 0);
};

/** A door, window or opening; an edge segment of a type this version doesn't know is left out of measurements. */
const isMeasuredOpening = (segment: FloorPlanEdgeSegmentSimple): boolean =>
  segment.edgeSegmentType !== 'alcove' && isKnownEdgeSegmentType(segment.edgeSegmentType);

/** Every door, window and opening in the room, on every host that holds them (`lineHosts`). */
const wallOpenings = (shape: FloorPlanRoom): FloorPlanEdgeSegmentSimple[] => lineHosts(shape).flatMap(host => host.segments.filter(isMeasuredOpening));

/**
 * How far up the wall an opening runs from its base, and whether it reaches
 * the ceiling. With no `openingHeight` an opening runs to the ceiling; with
 * one it stops there or at the ceiling, whichever is lower. In a room with no
 * ceiling height an opening runs its own height, and only one with no height
 * of its own reaches the ceiling.
 */
function openingRise(opening: FloorPlanEdgeSegmentSimple, base: number, ceiling: number): { extent: number; reachesCeiling: boolean } {
  if (ceiling <= 0) return { extent: opening.openingHeight ?? 0, reachesCeiling: opening.openingHeight == null };
  const reach = Math.max(0, ceiling - base);
  const extent = opening.openingHeight != null ? Math.min(opening.openingHeight, reach) : reach;
  return { extent, reachesCeiling: base + extent >= ceiling };
}

/** What one door, window or opening takes out of the room's measurements, and the trim it adds (ft, ft²). */
interface OpeningMeasures {
  /** The run it cuts out of the perimeter and baseboard: a door's, or an opening's that reaches the floor. */
  perimeter: number;
  /** The wall face it cuts out. */
  wall: number;
  /** The run it cuts out of the crown, when it reaches the ceiling. */
  crown: number;
  /** Casing on this room's side of a door: both sides and the head. */
  doorTrim: number;
  /** Casing around a window. */
  windowTrim: number;
  windowSill: number;
}

/** One opening's measures: it cuts the wall it sits in; doors and windows are cased on this room's side, and windows have a sill. */
function openingMeasures(opening: FloorPlanEdgeSegmentSimple, ceiling: number): OpeningMeasures {
  const base = Math.max(0, segmentOpeningBase(opening));
  const { extent, reachesCeiling } = openingRise(opening, base, ceiling);
  const { width, edgeSegmentType } = opening;
  const isDoor = edgeSegmentType === 'door', isWindow = edgeSegmentType === 'window';
  return {
    perimeter: isDoor || (!isWindow && base <= 0) ? width : 0,
    wall: width * extent,
    crown: reachesCeiling ? width : 0,
    doorTrim: isDoor ? width + 2 * extent : 0,
    windowTrim: isWindow ? 2 * (width + extent) : 0,
    windowSill: isWindow ? width : 0,
  };
}

export const measureFloorPlanRoom = (shape: FloorPlanRoom): MeasurementResult => {
  const scene = resolveScene(shape);
  const ceiling = roomCeilingHeight(shape);
  const openings = wallOpenings(shape).map(opening => openingMeasures(opening, ceiling));
  const total = (key: keyof OpeningMeasures) => openings.reduce((sum, measures) => sum + measures[key], 0);
  const builtIns = builtInWallCoverage(roomFreeElements(shape), scene.segments, ceiling);
  const perimeter = Math.max(0, perimeterLength(scene.segments) - total('perimeter'));

  return {
    totalFloorArea: multiPolygonArea(scene.roomSolid),
    perimeter,
    wallArea: Math.max(0, wallAreaOf(scene.segments) - total('wall') - builtIns.wallArea),
    areaByType: scene.floors.map(f => ({ type: f.type, area: f.area })),
    unflooredArea: scene.unflooredArea,
    baseboardLength: Math.max(0, perimeter - builtIns.baseboardLength),
    crownLength: Math.max(0, perimeterLength(scene.segments) - total('crown')),
    doorTrimLength: total('doorTrim'),
    windowTrimLength: total('windowTrim'),
    windowSillLength: total('windowSill'),
    countertopArea: scene.counterArea,
  };
};
